import { useState, useEffect, useMemo, useRef } from "react";
import {
  Dialog,
  Button,
  TextField,
  MenuItem,
  Box,
  Typography,
  IconButton,
  Skeleton,
  Tooltip,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { Close, Save, MyLocation, Search } from "@mui/icons-material";
import {
  MapContainer,
  TileLayer,
  Marker,
  useMapEvents,
  useMap,
} from "react-leaflet";
import L from "leaflet";
import { toast } from "react-toastify";
import theme from "../../theme/theme";
import ZoneSelect from "../common/ZoneSelect";
import CitySelect from "../common/CitySelect";

// Leaflet icon setup
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

function LocationMarker({ position, setPosition }) {
  useMapEvents({
    click(e) {
      setPosition(e.latlng);
    },
  });

  const markerRef = useRef(null);
  const eventHandlers = useMemo(
    () => ({
      dragend() {
        const marker = markerRef.current;
        if (marker != null) {
          setPosition(marker.getLatLng());
        }
      },
    }),
    [setPosition],
  );

  return position === null ? null : (
    <Marker
      draggable={true}
      eventHandlers={eventHandlers}
      position={position}
      ref={markerRef}
    />
  );
}

function ChangeView({ center, zoom }) {
  const map = useMap();
  useEffect(() => {
    map.setView(center, zoom);
  }, [center, map, zoom]);
  return null;
}

export const EditProjectDialog = ({
  open,
  onClose,
  loading,
  saving,
  project,
  setProject,
  onSave,
  errors = {},
}) => {
  const [position, setPosition] = useState(null);
  const [mapCenter, setMapCenter] = useState([17.385, 78.4867]);
  const [mapZoom, setMapZoom] = useState(12);
  const [searchQuery, setSearchQuery] = useState("");

  // Initialize map position from project data
  useEffect(() => {
    if (project?.latitude && project?.longitude) {
      const pos = {
        lat: parseFloat(project.latitude),
        lng: parseFloat(project.longitude),
      };
      setPosition(pos);
      setMapCenter([pos.lat, pos.lng]);
      setMapZoom(15);
    } else {
      setPosition(null);
      setMapCenter([17.385, 78.4867]);
      setMapZoom(12);
    }
    setSearchQuery("");
  }, [project?.id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "zone_id") {
      setProject({ ...project, zone_id: value, city_id: "" });
    } else if (name === "lift_type") {
      // Goods lifts are quoted by load (kg), not persons.
      setProject({
        ...project,
        lift_type: value,
        capacity_persons: value === "goods" ? "" : project.capacity_persons,
      });
    } else if (name === "door_type") {
      // Vision type only applies to automatic doors; swing only to manual doors.
      setProject({
        ...project,
        door_type: value,
        vision_type: value === "automatic" ? project.vision_type : "",
        is_swing_door: value === "manual" ? project.is_swing_door : false,
      });
    } else {
      setProject({ ...project, [name]: value });
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    try {
      const resp = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}`,
      );
      const data = await resp.json();
      if (data && data.length > 0) {
        const { lat, lon } = data[0];
        const newPos = { lat: parseFloat(lat), lng: parseFloat(lon) };
        setMapCenter([newPos.lat, newPos.lng]);
        setMapZoom(15);
        setPosition(newPos);
      } else {
        toast.warning("Location not found");
      }
    } catch (err) {
      toast.error("Geocoding failed");
    }
  };

  const handleGetCurrentLocation = () => {
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const newPos = {
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
          };
          setMapCenter([newPos.lat, newPos.lng]);
          setMapZoom(16);
          setPosition(newPos);
        },
        () => toast.error("Failed to retrieve location."),
        { enableHighAccuracy: true },
      );
    } else {
      toast.error("Geolocation is not supported by this browser.");
    }
  };

  const handleSave = () => {
    const payload = { ...project };
    if (position) {
      payload.latitude = position.lat;
      payload.longitude = position.lng;
    }
    // Call the parent's save with the enriched payload
    onSave(payload);
  };

  return (
    <Dialog
      open={open}
      onClose={!saving ? onClose : undefined}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: theme.dialog.borderRadius,
          minWidth: theme.dialog.minWidth,
        },
      }}
    >
      <Box
        sx={{
          backgroundColor: theme.dialog.headerBgColor,
          p: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box>
          <Typography
            variant="h6"
            fontWeight={"bold"}
            color={theme.palette.primary.main}
          >
            Edit Project
          </Typography>
        </Box>
        <Box>
          <IconButton onClick={!saving ? onClose : undefined} disabled={saving}>
            <Close />
          </IconButton>
        </Box>
      </Box>
      <Box sx={{ p: 4, maxHeight: "70vh", overflowY: "auto" }}>
        {loading ? (
          <Box>
            {[...Array(7)].map((_, i) => (
              <Skeleton key={i} height={56} sx={{ mb: 1 }} />
            ))}
          </Box>
        ) : (
          <>
            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
              <TextField
                fullWidth
                label="Project ID"
                name="project_id"
                value={project?.project_id || ""}
                onChange={handleChange}
                disabled={saving}
                error={!!errors.project_id}
                helperText={errors.project_id?.[0]}
              />
              <TextField
                fullWidth
                label="Project Name"
                name="project_name"
                value={project?.project_name || ""}
                onChange={handleChange}
                disabled={saving}
                error={!!errors.project_name}
                helperText={errors.project_name?.[0]}
              />
            </Box>
            <ZoneSelect
              fullWidth
              value={project?.zone_id || ""}
              selectedOption={project?.zone || null}
              onChange={(id) =>
                setProject({ ...project, zone_id: id, city_id: "" })
              }
              disabled={saving}
              error={!!errors.zone_id}
              helperText={errors.zone_id?.[0]}
              sx={{ mb: 2 }}
            />
            <CitySelect
              fullWidth
              zoneId={project?.zone_id || ""}
              value={project?.city_id || ""}
              selectedOption={project?.city || null}
              onChange={(id) => setProject({ ...project, city_id: id })}
              disabled={saving}
              error={!!errors.city_id}
              helperText={
                errors.city_id?.[0] ||
                (!project?.zone_id ? "Select a zone first" : "")
              }
              sx={{ mb: 2 }}
            />
            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
              <TextField
                select
                fullWidth
                label="Lift Type"
                name="lift_type"
                value={project?.lift_type || ""}
                onChange={handleChange}
                disabled={saving}
                error={!!errors.lift_type}
                helperText={errors.lift_type?.[0]}
              >
                <MenuItem value="passenger">Passenger</MenuItem>
                <MenuItem value="freight">Freight</MenuItem>
                <MenuItem value="home">Home</MenuItem>
                <MenuItem value="hospital">Hospital</MenuItem>
                <MenuItem value="goods">Goods</MenuItem>
                <MenuItem value="stretcher">Stretcher</MenuItem>
              </TextField>
              <TextField
                select
                fullWidth
                label="Status"
                name="status"
                value={project?.status || ""}
                onChange={handleChange}
                disabled={saving}
                error={!!errors.status}
                helperText={errors.status?.[0]}
              >
                <MenuItem value="under_construction">
                  Under Construction
                </MenuItem>
                <MenuItem value="handed_over">Handed Over</MenuItem>
                <MenuItem value="under_service_plan">
                  Under Service Plan
                </MenuItem>
              </TextField>
            </Box>
            <TextField
              fullWidth
              label="Cabin Type"
              name="cabin_type"
              value={project?.cabin_type || ""}
              onChange={handleChange}
              disabled={saving}
              sx={{ mb: 2 }}
            />
            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
              {project?.lift_type !== "goods" && (
                <TextField
                  fullWidth
                  label="Capacity (Persons)"
                  name="capacity_persons"
                  type="number"
                  value={project?.capacity_persons || ""}
                  onChange={handleChange}
                  disabled={saving}
                />
              )}
              <TextField
                fullWidth
                label="Capacity (Kg)"
                name="capacity_kg"
                type="number"
                value={project?.capacity_kg || ""}
                onChange={handleChange}
                disabled={saving}
              />
            </Box>
            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
              <TextField
                fullWidth
                label="No. of Floors"
                name="no_of_floors"
                type="number"
                value={project?.no_of_floors || ""}
                onChange={handleChange}
                disabled={saving}
              />
              <TextField
                fullWidth
                label="No. of Basements"
                name="no_of_basements"
                type="number"
                value={project?.no_of_basements || ""}
                onChange={handleChange}
                disabled={saving}
              />
            </Box>
            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
              <TextField
                fullWidth
                label="Shaft Length"
                name="shaft_length"
                value={project?.shaft_length || ""}
                onChange={handleChange}
                disabled={saving}
              />
              <TextField
                fullWidth
                label="Shaft Width"
                name="shaft_width"
                value={project?.shaft_width || ""}
                onChange={handleChange}
                disabled={saving}
              />
            </Box>
            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
              <TextField
                select
                fullWidth
                label="Door Type"
                name="door_type"
                value={project?.door_type || ""}
                onChange={handleChange}
                disabled={saving}
              >
                <MenuItem value="manual">Manual</MenuItem>
                <MenuItem value="automatic">Automatic</MenuItem>
              </TextField>
              {project?.door_type === "manual" && (
                <FormControlLabel
                  sx={{ minWidth: 120 }}
                  control={
                    <Checkbox
                      checked={!!project?.is_swing_door}
                      onChange={(e) =>
                        setProject({
                          ...project,
                          is_swing_door: e.target.checked,
                        })
                      }
                      disabled={saving}
                    />
                  }
                  label="Swing"
                />
              )}
              {project?.door_type === "automatic" && (
                <TextField
                  select
                  fullWidth
                  label="Vision Type"
                  name="vision_type"
                  value={project?.vision_type || ""}
                  onChange={handleChange}
                  disabled={saving}
                >
                  <MenuItem value="big_vision">Big Vision</MenuItem>
                  <MenuItem value="small_vision">Small Vision</MenuItem>
                  <MenuItem value="half_vision">Half Vision</MenuItem>
                  <MenuItem value="no_vision">No Vision</MenuItem>
                </TextField>
              )}
              <TextField
                select
                fullWidth
                label="Lift Configuration"
                name="lift_configuration"
                value={project?.lift_configuration || ""}
                onChange={handleChange}
                disabled={saving}
              >
                <MenuItem value="mr">MR</MenuItem>
                <MenuItem value="semi-mrl">Semi-MRL</MenuItem>
                <MenuItem value="mrl">MRL</MenuItem>
                <MenuItem value="hydraulic">Hydraulic</MenuItem>
                <MenuItem value="belt">Belt</MenuItem>
              </TextField>
            </Box>
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Additional Notes"
              name="additional_notes"
              value={project?.additional_notes || ""}
              onChange={handleChange}
              disabled={saving}
              sx={{ mb: 2 }}
            />

            {/* Map Section */}
            <Typography variant="subtitle2" fontWeight="600" sx={{ mb: 1 }}>
              Project Location
            </Typography>
            <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
              <TextField
                size="small"
                fullWidth
                placeholder="Search location..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              />
              <Tooltip title="Search">
                <IconButton onClick={handleSearch} color="primary">
                  <Search />
                </IconButton>
              </Tooltip>
              <Tooltip title="My Location">
                <IconButton onClick={handleGetCurrentLocation} color="primary">
                  <MyLocation />
                </IconButton>
              </Tooltip>
            </Box>
            <Box
              sx={{
                height: 300,
                borderRadius: 2,
                overflow: "hidden",
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <MapContainer
                center={mapCenter}
                zoom={mapZoom}
                style={{ height: "100%", width: "100%" }}
              >
                <ChangeView center={mapCenter} zoom={mapZoom} />
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                <LocationMarker position={position} setPosition={setPosition} />
              </MapContainer>
            </Box>
            {position && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 0.5, display: "block" }}
              >
                Lat: {position.lat.toFixed(6)}, Lng: {position.lng.toFixed(6)}
              </Typography>
            )}
          </>
        )}
      </Box>
      <Box
        sx={{
          backgroundColor: theme.dialog.headerBgColor,
          p: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Button
          variant="contained"
          onClick={onClose}
          disabled={saving}
          sx={{
            backgroundColor: theme.button.backgroundColor,
            color: "#000",
            borderRadius: theme.button.borderRadius,
          }}
        >
          Close
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || loading}
          sx={{
            backgroundColor: theme.button.editBackgroundColor,
            borderRadius: theme.button.borderRadius,
          }}
          startIcon={<Save />}
        >
          {saving ? "Saving..." : "Save Changes"}
        </Button>
      </Box>
    </Dialog>
  );
};
